import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { authEvents, type SignedOutReason } from '@/api/auth-events';
import { unwrap } from '@/api/envelope';
import { portalLogout, portalMe } from '@/api/generated';
import type { PortalProfile } from '@/api/generated/types.gen';
import { organizationScope } from '@/api/organization-scope';
import { endSession } from '@/api/refresh';
import {
  canShareSessions,
  createSessionChannel,
  type RenewedTokens,
  type SessionChannel,
} from '@/api/session-channel';
import { tokenStore } from '@/api/token-store';

import {
  AuthContext,
  type AuthContextValue,
  type CurrentUser,
  type OrganizationNotice,
} from './auth-context';
import { toCurrentUser } from './profile';
import { ROLES } from './roles';
import { signOutNotice } from './signOutNotice';

const SESSION_KEY = 'activistarc.portal.user';

/**
 * Whether what the tab stored is a user this build understands.
 *
 * A session stored by an older build may lack a field this one reads, and a
 * cast would let it through to throw somewhere unrelated. One that does not
 * fit is treated as no session: the person signs in again, which is the
 * right answer for a stored shape nobody can vouch for. The same check
 * guards a sign-in another tab hands over.
 */
function isCurrentUser(value: unknown): value is CurrentUser {
  if (!value || typeof value !== 'object') return false;
  const user = value as Record<string, unknown>;
  return (
    typeof user.id === 'string' &&
    typeof user.name === 'string' &&
    typeof user.email === 'string' &&
    Object.values(ROLES).includes(user.role as (typeof ROLES)[keyof typeof ROLES]) &&
    Array.isArray(user.organizations) &&
    (user.organizationId === null || typeof user.organizationId === 'string') &&
    (user.organizationName === null || typeof user.organizationName === 'string')
  );
}

function readStoredUser(): CurrentUser | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isCurrentUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The signed-in person, derived from the stored session.
 *
 * The user is kept alongside the tokens rather than decoded from them, because
 * what the portal needs to show - a name, an organization - is not all in the
 * token, and decoding a token to trust its contents client-side is the wrong
 * habit to build.
 *
 * The role is real for all three kinds of account now, and it is the role held
 * in the organization currently open. Somebody may be a Super Admin of one
 * organization and an Admin of another, so switching organization switches
 * their role with it.
 *
 * VITE_DEV_ROLE used to stand in for organization Super Admins and Admins,
 * who had no accounts on the backend. They have them now, so that fallback is
 * gone - it would have overridden a real Super Admin's role with whatever the
 * developer happened to have set.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Reading the stored session is synchronous, so it is done as the initial
  // state rather than in an effect. Doing it in an effect would render once
  // signed out and then again signed in, which flashes the sign-in screen.
  // Only trust a stored user if the tokens are still there too.
  const [user, setUser] = useState<CurrentUser | null>(() =>
    tokenStore.get() ? readStoredUser() : null,
  );
  const queryClient = useQueryClient();

  /*
   * A tab with no sign-in of its own asks the browser's other tabs for theirs
   * before deciding nobody is signed in (portal-session-rules, B8, D8). Where
   * tabs cannot talk there is nobody to ask, so nothing to wait for.
   */
  const [loading, setLoading] = useState(() => !tokenStore.get() && canShareSessions());

  // The person as of the last change, for the code that runs outside a
  // render: the channel's handlers, a reload's answer, the sign-out listener.
  const userRef = useRef(user);

  const [organizationNotice, setOrganizationNotice] = useState<OrganizationNotice | null>(
    null,
  );
  const dismissOrganizationNotice = useCallback(() => setOrganizationNotice(null), []);

  /*
   * The account pages and the activity log cache under keys that name no
   * organization, so what one organization loaded would stay on screen - and
   * be saved against - under another's name. Reset, which also refetches the
   * open page; a request in flight for the old organization is cancelled
   * first so its answer cannot land afterwards.
   */
  const resetOrganizationData = useCallback(() => {
    void queryClient
      .cancelQueries({ queryKey: ['account'] })
      .then(() => queryClient.resetQueries({ queryKey: ['account'] }));
    void queryClient
      .cancelQueries({ queryKey: ['activity'] })
      .then(() => queryClient.resetQueries({ queryKey: ['activity'] }));
  }, [queryClient]);

  /*
   * Keeps the person this tab shows. The organization every request names
   * follows the open organization, and is written only when that changes:
   * a staff member viewing an organization has the viewed one in the scope
   * (ViewingAsProvider), which a change to their name must not clear (D7).
   */
  const keep = useCallback((next: CurrentUser) => {
    const previous = userRef.current;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    if (!previous || previous.organizationId !== next.organizationId) {
      organizationScope.set(next.organizationId);
    }
    userRef.current = next;
    setUser(next);
  }, []);

  /*
   * The person as the server just described them - from /me on a page load,
   * or from a renewal here or in another tab (portal-session-rules, B12, I8).
   * A role changed in the open organization takes effect at once. The open
   * organization lost: moved to the one left, asked among several, or signed
   * out with none (B14, D16). A role this build does not know changes
   * nothing, as at sign-in, where it is refused rather than guessed at.
   */
  const applyProfile = useCallback(
    (profile: PortalProfile) => {
      const current = userRef.current;
      if (!current || profile.id !== current.id) return;
      const outcome = toCurrentUser(profile, current.organizationId);
      if (outcome.kind === 'unknownRole') return;
      if (outcome.kind === 'user') {
        // The usual answer is "nothing changed". Keeping the same person then
        // spares every screen that reads them a needless redraw.
        if (JSON.stringify(outcome.user) !== JSON.stringify(current)) keep(outcome.user);
        return;
      }

      const lostName = current.organizationName ?? 'that organization';
      const { remaining, userIn } = outcome;
      if (remaining.length === 0) {
        endSession('no-organization');
        return;
      }
      if (remaining.length === 1) {
        keep(userIn(remaining[0]));
        resetOrganizationData();
        setOrganizationNotice({ kind: 'switched', lostName });
        return;
      }
      // Several left: the list is brought up to date, and the person chooses
      // which to open before anything else (OrganizationAccessNotice).
      keep({ ...current, organizations: remaining });
      setOrganizationNotice({ kind: 'choose', lostName, memberships: remaining });
    },
    [keep, resetOrganizationData],
  );

  // A renewal in this tab says who the person is now (B12).
  useEffect(() => authEvents.onProfileUpdated(applyProfile), [applyProfile]);

  /*
   * Once per page load, ask the server who is signed in (B12). The person
   * this tab stored may be out of date: demoted, added to an organization,
   * removed from one. A failure to ask changes nothing (I9); a refusal is
   * handled by the request wrapper, which signs the person out and says why.
   */
  const signedIn = user !== null;
  const reloaded = useRef(false);
  // An answer that arrives after this provider is gone is about a page that
  // no longer exists, and must not write into the tab's storage.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!signedIn || reloaded.current) return;
    reloaded.current = true;
    portalMe({ throwOnError: true })
      .then((reply) => {
        if (mounted.current) applyProfile(unwrap(reply.data));
      })
      .catch(() => undefined);
  }, [signedIn, applyProfile]);

  const signIn = useCallback(
    (next: CurrentUser) => {
      // A new sign-in in this tab: the last one's notice and question are
      // spent (I7, B14).
      signOutNotice.clear();
      setOrganizationNotice(null);
      // Every sign-in knows its own role, whichever of the three it is.
      keep(next);
    },
    [keep],
  );

  const switchOrganization = useCallback(
    (organizationId: string) => {
      if (!user) return;
      const chosen = user.organizations.find((o) => o.id === organizationId);
      // An id they do not belong to is ignored. The backend would refuse it
      // anyway, but changing the visible organization to one they cannot read
      // would leave every page showing a not-found.
      if (!chosen) return;
      if (chosen.id === user.organizationId) return;

      const next: CurrentUser = {
        ...user,
        organizationId: chosen.id,
        organizationName: chosen.name,
        // Their role travels with the organization, so this is the whole
        // reason switching is more than a label change.
        role: chosen.role,
      };
      /*
       * Side effects here, not inside the state updater: React may run an
       * updater twice, and these must run exactly once. The scope first, so
       * that anything asked for from here on names the new organization; then
       * what the old one had loaded is reset.
       */
      keep(next);
      resetOrganizationData();
    },
    [user, keep, resetOrganizationData],
  );

  // This tab's end of the channel the tabs share, while it is open.
  const channelRef = useRef<SessionChannel | null>(null);

  /*
   * Ends the session in this tab, however it ended - Log Out, a refusal, the
   * server's word, or another tab. The sign-in page is told why and whose
   * session it was (I6, I7); the other tabs are told too, with whose it was,
   * unless another tab is what told this one (I4). Called again once it has
   * ended, it does nothing, so nothing is said or posted twice.
   */
  const endHere = useCallback((reason: SignedOutReason, broadcast: boolean) => {
    const person = userRef.current;
    if (!person) return;
    signOutNotice.write(reason, person.id);
    if (broadcast) channelRef.current?.postSignedOut(reason, person.id);
    sessionStorage.removeItem(SESSION_KEY);
    organizationScope.clear();
    setOrganizationNotice(null);
    userRef.current = null;
    setUser(null);
  }, []);

  const signOut = useCallback(() => {
    /*
     * Clear what this browser holds first, so pressing Log Out always signs the
     * person out here whatever the network does. Then end the session on the
     * server, using the token captured before it was cleared.
     *
     * That capture is the point. The request used to be started without being
     * waited for and the tokens thrown away on the very next line - but the
     * generated client pauses at an `await` before it reads the token, so by
     * the time it looked there was nothing there. The request went out with no
     * identification, the server answered 401, and the session stayed alive.
     * `catch(() => undefined)` hid it. Anybody holding a copy of the token
     * could carry on using it, which is the opposite of what signing out means.
     */
    const tokens = tokenStore.get();

    // Ended here first, at once, telling the other tabs, which share this
    // sign-in (B10, D5). Then the tokens go and the rest of the app is told;
    // this tab's own listener finds nothing left to end.
    endHere('manual', true);
    if (tokens) endSession('manual', { broadcast: false });
    else authEvents.emitSignedOut('manual', { broadcast: false });

    /*
     * The refresh token names the session, so Log Out still ends it after 30
     * idle minutes, when the access token has expired and would be refused
     * (portal-session-rules, D13). No access token is sent: the store is
     * already empty, so the request wrapper adds none.
     */
    if (!tokens) return;
    void portalLogout({ body: { refresh_token: tokens.refreshToken } }).catch(
      () => undefined,
    );
  }, [endHere]);

  /*
   * The browser's other tabs (design Decisions 1 and 2), and every ending of
   * this tab's session. Opened here rather than when the provider is created,
   * so the channel lives exactly as long as the subscriptions on it.
   */
  useEffect(() => {
    const channel = createSessionChannel();
    channelRef.current = channel;
    let current = true;

    // A session the API layer ended: a refusal, the server's word, or another
    // tab's sign-out.
    const stopEnding = authEvents.onSignedOut((reason, { broadcast }) =>
      endHere(reason, broadcast),
    );

    // Answer a new tab with this tab's sign-in, when it has one. The open
    // organization goes with it; staff "view portal" mode does not (D7, I1).
    const stopAnswering = channel.answerWith(() => {
      const tokens = tokenStore.get();
      const person = userRef.current;
      return tokens && person
        ? { tokens, user: person, organizationId: person.organizationId }
        : null;
    });

    /*
     * Another tab renewed: take its pair, so this tab never presents the
     * refresh token that renewal retired (I2, B9), and the person as the
     * renewal described them; its own organization stays. Only for the same
     * person: two tabs of one browser can hold different people, when one
     * asked and nobody answered. A tab still asking keeps the newest pair it
     * hears, in case the answer it takes is older (I2).
     */
    let heardWhileAsking: RenewedTokens | null = null;
    const stopTokens = channel.onTokens((renewed) => {
      const person = userRef.current;
      if (!tokenStore.get() || !person) {
        heardWhileAsking = renewed;
        return;
      }
      if (renewed.profile?.id !== person.id) return;
      tokenStore.set(renewed.tokens);
      applyProfile(renewed.profile);
    });

    // Another tab's session ended, and this one shares it if it is the same
    // person's (B10). Not told back to the others: they have heard (I4).
    const stopSignedOut = channel.onSignedOut(({ reason, userId }) => {
      if (userRef.current && userRef.current.id === userId) {
        endSession(reason, { broadcast: false });
      }
    });

    if (!tokenStore.get() && channel.available) {
      void channel.ask(500).then((snapshot) => {
        if (!current) return;
        if (snapshot && isCurrentUser(snapshot.user) && !tokenStore.get()) {
          const newer =
            heardWhileAsking?.profile?.id === snapshot.user.id
              ? heardWhileAsking.tokens
              : null;
          tokenStore.set(newer ?? snapshot.tokens);
          // The organization of the tab that answered (B11, I5).
          signIn({ ...snapshot.user, organizationId: snapshot.organizationId });
        }
        heardWhileAsking = null;
        setLoading(false);
      });
    }

    return () => {
      current = false;
      channelRef.current = null;
      stopEnding();
      stopAnswering();
      stopTokens();
      stopSignedOut();
      channel.close();
    };
  }, [signIn, applyProfile, endHere]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signIn,
      switchOrganization,
      signOut,
      organizationNotice,
      dismissOrganizationNotice,
    }),
    [
      user,
      loading,
      signIn,
      switchOrganization,
      signOut,
      organizationNotice,
      dismissOrganizationNotice,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
